import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { PercentageDiscountPreview } from "@/features/product/summary/components/PercentageDiscountPreview"
import { PricePreview, PricePreviewType } from "@/features/product/summary/components/PricePreview"
import { ProductRating } from "@/features/product/summary/components/ProductRating"
import { Thumbnail } from "@/features/product/summary/components/Thumbnail"
import type { ThumbnailSize } from "@/features/product/summary/components/Thumbnail"

interface ProductPreviewTemplateProps {
  title: string
  thumbnail?: string | null
  thumbnailSize?: ThumbnailSize
  href?: string
  percentageDiscount?: number
  isFeatured?: boolean
  hasFreeShipping?: boolean
  originalPrice?: string
  calculatedPrice?: string
  pagosPriceLabel?: string
  onClick?: () => void
  rating: {
    count_responses: number
    average_rating: number
  } | null
}

export const ProductPreviewTemplate = ({
  title,
  href = "/",
  thumbnail,
  thumbnailSize = "full",
  isFeatured,
  percentageDiscount,
  hasFreeShipping,
  originalPrice,
  calculatedPrice,
  pagosPriceLabel,
  rating,
  onClick,
}: ProductPreviewTemplateProps) => {
  return (
    <LocalizedClientLink key={`${title}_${href}`} href={href} onClick={onClick} className="group">
      <div>
        <div className="relative mb-2">
          {!!percentageDiscount && <PercentageDiscountPreview percentageDiscount={percentageDiscount} />}
          <div className="border-line-card shadow-soft relative overflow-hidden rounded-3xl border">
            <Thumbnail thumbnail={thumbnail} size={thumbnailSize} isFeatured={isFeatured} />
            <div className="pointer-events-none absolute inset-0 bg-gray-500 opacity-0 transition-opacity duration-150 group-active:opacity-30"></div>
          </div>
        </div>
        {originalPrice && (
          <PricePreview
            type={originalPrice && calculatedPrice && originalPrice !== calculatedPrice ? PricePreviewType.DISCOUNT_STRIKE_THROUGH : PricePreviewType.DEFAULT}
            originalPrice={originalPrice}
            calculatedPrice={calculatedPrice}
          />
        )}
        {pagosPriceLabel && <p className="text-nelo-blue mb-2 text-[14px] font-medium tracking-normal">4 pagos de {pagosPriceLabel}</p>}
        <div className="mb-1 flex flex-wrap gap-2">
          <div className="z-10 flex w-fit items-center gap-x-1 rounded-md bg-[#E1F4FF] px-1 py-0.5 text-white">
            <span className="text-nelo-blue text-xs font-medium">Sin intereses</span>
          </div>
          {hasFreeShipping && (
            <div className="z-10 flex w-fit items-center gap-x-1 rounded-md bg-[#E5FAE9] px-1 py-0.5 text-white">
              <span className="text-xs font-medium text-[#256A34]">Envío gratis</span>
            </div>
          )}
        </div>

        <div className="txt-compact-medium">
          <p className="font-inter text-nelo-dark-gray line-clamp-1 leading-tight font-normal">{title}</p>
          {rating && rating.average_rating > 2 && (
            <div className="text-[10px] lg:text-sm">
              <ProductRating rating={rating.average_rating} count_responses={rating.count_responses} />
            </div>
          )}
        </div>
      </div>
    </LocalizedClientLink>
  )
}
